import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Helper function to check if user has a specific permission
  const hasPermission = (permissionKey) => {
    // Super admin bypasses all permission checks
    if (user?.role === 'superadmin' || user?.role === 'super_admin') {
      return true;
    }
    
    // Apply permission checks to admin and instructor roles
    if (user?.role === 'admin' || user?.role === 'instructor') {
      if (!permissionKey || typeof permissionKey !== 'string') {
        return false;
      }
      
      // Check if permission exists in the permissions array or object
      if (Array.isArray(permissions)) {
        return permissions.includes(permissionKey);
      } else if (typeof permissions === 'object' && permissions !== null) {
        return permissions[permissionKey] === true || permissions[permissionKey] === 'allow';
      }
      
      return false;
    }
    
    // Students and other roles bypass permission checks for now
    return true;
  };

  // Helper function to check multiple permissions (AND logic)
  const hasAllPermissions = (permissionKeys) => {
    if (!permissionKeys || !Array.isArray(permissionKeys)) return false;
    return permissionKeys.every(key => key && hasPermission(key));
  };

  // Helper function to check if user has any of the specified permissions (OR logic)
  const hasAnyPermission = (permissionKeys) => {
    if (!permissionKeys || !Array.isArray(permissionKeys)) return false;
    return permissionKeys.some(key => key && hasPermission(key));
  };

  // Update permissions when user data changes (login/logout)
  useEffect(() => {
    if (user) {
      // Use permissions from auth response for admin and instructor users
      if (user.role === 'admin' || user.role === 'instructor') {
        // Permissions should come from the user object returned by /auth/me
        // or from the AccessContext which loads from /my-institute/access
        if (user.permissions && Array.isArray(user.permissions)) {
          setPermissions(user.permissions);
        } else {
          setPermissions([]);
        }
        setLoading(false);
      } 
      // For other roles (students, etc.), set empty permissions
      else {
        setPermissions([]);
        setLoading(false);
      }
    } else {
      // Clear permissions when user logs out
      setPermissions([]);
      setLoading(false);
    }
  }, [user]);

  // Update a specific permission (for real-time updates)
  const updatePermission = (permissionKey, effect) => {
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: effect === 'allow' || effect === 'grant' ? 'allow' : 'deny'
    }));
  };

  const value = {
    permissions,
    loading,
    user,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    updatePermission
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
